import { Check, AlertTriangle, Info, X, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';

type FindingStatus = 'pass' | 'warning' | 'fail' | 'info';

interface KeyFindingProps {
  finding: string;
  status: FindingStatus;
  details?: string;
  linkedSection?: string;
  onClick?: () => void;
}

const statusConfig: Record<
  FindingStatus,
  {
    icon: typeof Check;
    bgClass: string;
    iconClass: string;
    borderClass: string;
  }
> = {
  pass: {
    icon: Check,
    bgClass: 'bg-risk-low-bg',
    iconClass: 'text-risk-low',
    borderClass: 'border-l-risk-low',
  },
  warning: {
    icon: AlertTriangle,
    bgClass: 'bg-risk-medium-bg',
    iconClass: 'text-risk-medium',
    borderClass: 'border-l-risk-medium',
  },
  fail: {
    icon: X,
    bgClass: 'bg-risk-critical-bg',
    iconClass: 'text-risk-critical',
    borderClass: 'border-l-risk-critical',
  },
  info: {
    icon: Info,
    bgClass: 'bg-risk-info-bg',
    iconClass: 'text-risk-info',
    borderClass: 'border-l-risk-info',
  },
};

const KeyFinding = ({ finding, status, details, linkedSection, onClick }: KeyFindingProps) => {
  const config = statusConfig[status];
  const Icon = config.icon;
  const isClickable = !!linkedSection || !!onClick;

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else if (linkedSection) {
      const element = document.getElementById(linkedSection);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  return (
    <div
      className={cn(
        'flex items-start gap-4 p-4 rounded-lg border-l-4 transition-all',
        config.bgClass,
        config.borderClass,
        isClickable && 'cursor-pointer hover:shadow-md hover:translate-x-1'
      )}
      onClick={isClickable ? handleClick : undefined}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={isClickable ? (e) => e.key === 'Enter' && handleClick() : undefined}
    >
      <div className={cn('p-2 rounded-full bg-white/50', config.iconClass)}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1">
        <p className="font-medium">{finding}</p>
        {details && <p className="text-sm text-muted-foreground mt-1">{details}</p>}
      </div>
      {isClickable && <ExternalLink className="w-4 h-4 text-muted-foreground mt-1" />}
    </div>
  );
};

export default KeyFinding;
